import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import { Slider } from '../../slider/Slider'
import useTranslation from '../../../../utils/translation'

const StyledTextareaWrapper = styled.div`
  ${(props) => props.theme.media.min.sm`
    display: ${theme(
      'questionBlock.textareaWrapper.displayBlock',
      'flex'
    )(props)};
    align-items: flex-end;
  `};
  margin-bottom: 14px;

  & > div {
    margin-bottom: 0;
  }
  ${(props) =>
    props.theme.questionBlock?.textareaWrapper?.flexBasis
      ? css`
          flex-basis: ${theme('questionBlock.textareaWrapper.flexBasis')};
        `
      : ''};
`

const OptionsContainer = styled.div`
  display: ${theme('options.container.display')};
  flex-direction: ${theme('options.container.flexDirection')};
  justify-content: ${theme('options.container.justifyContent')};
  flex-basis: ${(props) =>
    theme('questionBlock.options.container.flexBasis')(props) || '70%'};
  ${(props) =>
    props.active &&
    `
    color: ${props.theme.colors.link};
  `}
  ${(props) =>
    props.hasTextBoxedOption &&
    `
    display: flex;
    align-items: center;
  `}
`

const ErrorMessage = styled.span`
  color: ${theme('forms.colors.invalid')};
  font-size: ${theme('forms.errorMessage.fontSize')};
  text-align: left;
  display: block;
  padding-top: 7px;
`

const StyledSliderAreaWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`
const StyledSliderOptionsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
`

const SliderValueDisplay = styled.div`
  position: relative;
  text-align: center;
  font-size: ${theme('forms.fontSize')};
  font-weight: 600;
  color: ${theme('colors.darkGrey')};
  min-height: 20px;
`

export const FormSliderQuestion = ({ question, answers, onChange }) => {
  const t = useTranslation('app.form')
  const [value, setValue] = useState()

  const options = useMemo(() => question.options[0], [question.options])

  useEffect(() => {
    const defaultNumber = parseInt(answers[0]?.replace(/^\D+/g, ''), 10)
    if (isNaN(defaultNumber)) {
      setValue()
    } else {
      setValue(defaultNumber)
    }
  }, [answers])

  const parseValue = useCallback(
    (v) => {
      if (!v) {
        return '-'
      }
      const newValue = `${options.prefix} ${v}`
      if (v === options.maxValue && options.endSuffix) {
        return `${newValue}${options.endSuffix}`
      }
      return newValue
    },
    [options]
  )

  const onAfterChange = useCallback(
    (v) => {
      const newValue = parseValue(v)
      onChange([newValue])
    },
    [parseValue, onChange]
  )

  const parsedValue = useMemo(() => parseValue(value), [value, parseValue])
  const errorMessage = useMemo(() => {
    if (question.notAnswered === undefined) {
      return undefined
    }
    if (question.notAnswered) {
      return question.requiredText || t('required')
    }
    return undefined
  }, [question.notAnswered, question.requiredText, t])

  return (
    <>
      <OptionsContainer>
        <StyledSliderAreaWrapper>
          <StyledSliderOptionsWrapper>
            <span>{`${options.prefix} ${options.minValue}`}</span>
            <span>
              {`${options.prefix} ${options.maxValue}${options.endSuffix}`}
            </span>
          </StyledSliderOptionsWrapper>
          <StyledTextareaWrapper>
            <Slider
              minValue={options.minValue}
              maxValue={options.maxValue}
              value={value}
              amountInterval={options.amountInterval}
              onChange={setValue}
              onAfterChange={onAfterChange}
            />
          </StyledTextareaWrapper>
          {!!value && <SliderValueDisplay>{parsedValue}</SliderValueDisplay>}
        </StyledSliderAreaWrapper>
      </OptionsContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  )
}
